import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Layout, AdminLayout, ProtectedRoute } from './components/layout';
import { LoginPage, ForgotPasswordPage, ResetPasswordPage, RegisterPage } from './pages/auth';
import { ReferralPage } from './pages/referral/ReferralPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { TaskListPage, TaskDetailPage, MyTasksPage } from './pages/tasks';
import { CreditsPage } from './pages/credits/CreditsPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { GuidePage } from './pages/guide/GuidePage';
import { GuideDetailPage } from './pages/guide/GuideDetailPage';
import { AnnouncementsPage } from './pages/announcements/AnnouncementsPage';
import { AnnouncementDetailPage } from './pages/announcements/AnnouncementDetailPage';
import { useAuthStore } from './stores/authStore';

// Lazy load admin pages
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const AdminTasksPage = lazy(() => import('./pages/admin/AdminTasksPage').then(m => ({ default: m.AdminTasksPage })));
const AdminTaskFormPage = lazy(() => import('./pages/admin/AdminTaskFormPage').then(m => ({ default: m.AdminTaskFormPage })));
const AdminReviewsPage = lazy(() => import('./pages/admin/AdminReviewsPage').then(m => ({ default: m.AdminReviewsPage })));
const AdminPaymentsPage = lazy(() => import('./pages/admin/AdminPaymentsPage').then(m => ({ default: m.AdminPaymentsPage })));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminUserDetailPage = lazy(() => import('./pages/admin/AdminUserDetailPage').then(m => ({ default: m.AdminUserDetailPage })));
const AdminGuidePage = lazy(() => import('./pages/admin/AdminGuidePage').then(m => ({ default: m.AdminGuidePage })));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage').then(m => ({ default: m.AdminSettingsPage })));
const AdminActivityPage = lazy(() => import('./pages/admin/AdminActivityPage').then(m => ({ default: m.AdminActivityPage })));
const AdminLogsPage = lazy(() => import('./pages/admin/AdminLogsPage').then(m => ({ default: m.AdminLogsPage })));
const AdminReportPage = lazy(() => import('./pages/admin/AdminReportPage').then(m => ({ default: m.AdminReportPage })));
const AdminReferralsPage = lazy(() => import('./pages/admin/AdminReferralsPage').then(m => ({ default: m.AdminReferralsPage })));
const AdminAnnouncementsPage = lazy(() => import('./pages/admin/AdminAnnouncementsPage').then(m => ({ default: m.AdminAnnouncementsPage })));
const AdminPerformancePage = lazy(() => import('./pages/admin/AdminPerformancePage').then(m => ({ default: m.AdminPerformancePage })));
const AdminLocalGuidePage = lazy(() => import('./pages/admin/AdminLocalGuidePage').then(m => ({ default: m.AdminLocalGuidePage })));

// Loading spinner for lazy loaded pages
const PageLoader = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
  </div>
);

function App() {
  const { isAuthenticated } = useAuthStore();

  return (
    <>
      <Toaster position="top-right" richColors />
      <Routes>
      {/* Public auth routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/register"
        element={isAuthenticated ? <Navigate to="/" replace /> : <RegisterPage />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected routes with Layout */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/tasks" element={<TaskListPage />} />
        <Route path="/tasks/:id" element={<TaskDetailPage />} />
        <Route path="/my-tasks" element={<MyTasksPage />} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/referral" element={<ReferralPage />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/guide/:id" element={<GuideDetailPage />} />
        <Route path="/announcements" element={<AnnouncementsPage />} />
        <Route path="/announcements/:id" element={<AnnouncementDetailPage />} />
      </Route>

      {/* Admin routes with AdminLayout - lazy loaded */}
      <Route
        element={
          <ProtectedRoute requireAdmin>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense>} />
        <Route path="/admin/tasks" element={<Suspense fallback={<PageLoader />}><AdminTasksPage /></Suspense>} />
        <Route path="/admin/tasks/new" element={<Suspense fallback={<PageLoader />}><AdminTaskFormPage /></Suspense>} />
        <Route path="/admin/tasks/:id/edit" element={<Suspense fallback={<PageLoader />}><AdminTaskFormPage /></Suspense>} />
        <Route path="/admin/reviews" element={<Suspense fallback={<PageLoader />}><AdminReviewsPage /></Suspense>} />
        <Route path="/admin/payments" element={<Suspense fallback={<PageLoader />}><AdminPaymentsPage /></Suspense>} />
        <Route path="/admin/users" element={<Suspense fallback={<PageLoader />}><AdminUsersPage /></Suspense>} />
        <Route path="/admin/users/:id" element={<Suspense fallback={<PageLoader />}><AdminUserDetailPage /></Suspense>} />
        <Route path="/admin/local-guide" element={<Suspense fallback={<PageLoader />}><AdminLocalGuidePage /></Suspense>} />
        <Route path="/admin/guide" element={<Suspense fallback={<PageLoader />}><AdminGuidePage /></Suspense>} />
        <Route path="/admin/activity" element={<Suspense fallback={<PageLoader />}><AdminActivityPage /></Suspense>} />
        <Route path="/admin/reports" element={<Suspense fallback={<PageLoader />}><AdminReportPage /></Suspense>} />
        <Route path="/admin/performance" element={<Suspense fallback={<PageLoader />}><AdminPerformancePage /></Suspense>} />
        <Route path="/admin/referrals" element={<Suspense fallback={<PageLoader />}><AdminReferralsPage /></Suspense>} />
        <Route path="/admin/announcements" element={<Suspense fallback={<PageLoader />}><AdminAnnouncementsPage /></Suspense>} />
        <Route path="/admin/logs" element={<Suspense fallback={<PageLoader />}><AdminLogsPage /></Suspense>} />
        <Route path="/admin/settings" element={<Suspense fallback={<PageLoader />}><AdminSettingsPage /></Suspense>} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default App;
